import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { DepositRefundedAtInput } from "@/app/Common/Components/DepositRefundedAtInput";
import { HoldingDepositRefundedByInput } from "@/app/Common/Components/HoldingDepositRefundedByInput";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { ContactType, UpdateSupplierInput } from "@/gql/graphql";
import { ContactInput } from "@/app/Contacts/Components/ContactInput";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";
import { TaxTypeInput } from "@/app/Payments/Components/TaxTypeInput";
import { CurrencyTypeInput } from "@/app/Common/Components/CurrencyTypeInput";
import { SupplierRecord } from "@/app/Suppliers/GraphQL/supplierRecordQuery";

export function SupplierInvoiceAmountForm({
  supplier,
}: {
  supplier: SupplierRecord;
}) {
  const { values } = useFormState<UpdateSupplierInput>();

  return (
    <>
      <CurrencyTypeInput name={"billing_currency"} label={"Billing Currency"} />

      <CurrencyInput
        currency={values.billing_currency!}
        name={"charge_per_move_amount"}
        label={"Charge per move"}
      />

      {values.charge_per_move_amount ? (
        <SwitchInput
          name="confirm_delivery_before_billing"
          label="Confirm delivery"
          tooltip="Require confirmation of delivery before billing"
        />
      ) : null}

      <SwitchInput
        name="is_tax_included"
        label="Tax included in amount"
        tooltip="Is tax included in the amounts shown on the supplier's invoices?"
      />

      <TaxTypeInput name="tax_type" label="Tax type" />
    </>
  );
}

export function SupplierBillingEntityForm({
  supplier,
  label,
}: {
  supplier: SupplierRecord;
  label: string;
}) {
  return (
    <>
      {supplier.managedBy === null ? (
        <WarningBanner>
          <p className="text-sm">
            The supplier is not linked to any organisation
          </p>
        </WarningBanner>
      ) : null}

      <ContactInput
        name="billingEntity.connect"
        label={label}
        isEditable={!!supplier.managedBy}
        getQueryVariables={(search) => ({
          search,
          page: 1,
          first: 20,
          managedById: supplier.managedBy?.id,
          type: [ContactType.Entity, ContactType.Organisation],
        })}
        initialFixtureValues={{
          type: ContactType.Entity,
          parentContact: {
            connect: supplier.managedBy?.contact?.id ?? null!,
          },
        }}
      />
    </>
  );
}

export function SupplierHoldingDepositForm({
  supplier,
}: {
  supplier: SupplierRecord;
}) {
  const { values } = useFormState<UpdateSupplierInput>();

  return (
    <>
      <SwitchInput
        name="is_holding_deposit_required"
        label="Require holding deposit"
      />

      {values.is_holding_deposit_required ? (
        <>
          <CurrencyInput
            currency={supplier.currency}
            name={"holding_deposit_amount"}
            label={"Holding deposit"}
          />

          <DepositRefundedAtInput
            name="holding_deposit_refunded_at"
            label="Refunded at"
          />

          <HoldingDepositRefundedByInput
            name="holding_deposit_refunded_by"
            label="Refunded by"
          />
        </>
      ) : null}
    </>
  );
}

export function SupplierBookingFeeForm({
  supplier,
}: {
  supplier: SupplierRecord;
}) {
  return (
    <CurrencyInput
      label="Booking fee"
      name="booking_fee_amount"
      currency={supplier.currency}
    />
  );
}

export function SupplierFuelRefundForm({
  supplier,
}: {
  supplier: SupplierRecord;
}) {
  const { values } = useFormState<UpdateSupplierInput>();

  return (
    <>
      <SwitchInput
        name="imoova_refunds_fuel"
        label="Imoova refunds fuel"
        tooltip="When enabled, customers will receive an email to upload fuel receipts after delivery"
      />

      {values.imoova_refunds_fuel ? (
        <CurrencyInput
          currency={supplier.currency}
          name="fuel_refund_fee"
          label="Fuel refund fee"
        />
      ) : null}
    </>
  );
}

export function SupplierFerryRefundForm({
  supplier,
}: {
  supplier: SupplierRecord;
}) {
  const { values } = useFormState<UpdateSupplierInput>();

  return (
    <>
      <SwitchInput
        name="imoova_refunds_ferry"
        label="Imoova refunds ferry"
        tooltip="When enabled, customers will receive an email to upload a ferry receipt after delivery"
      />

      {values.imoova_refunds_ferry ? (
        <CurrencyInput
          currency={supplier.currency}
          name="ferry_refund_fee"
          label="Ferry refund fee"
        />
      ) : null}
    </>
  );
}
